// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

export const minecraftToolDarkTheme = {
  siteVariables: {
    bodyFontFamily: '"Noto Sans", "Segoe UI", system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif',
    colorScheme: {
      brand: {
        mc0: "#131313",
        mc1: "#212121",
        mc2: "#212121",
        mc3: "#373737",
        mc4: "#3a3a3a",
        mc5: "#4e4e4e",
        mcc1: "#ffffff",
        background: "#3c8527", // accent background for things like buttons. complemented with fore*4
        background1: "#312f2d", // main outermost background. in a dark theme, this should be dark. complemented with fore*1
        background2: "#484644",
        background3: "#5a5856",
        background4: "#8f8b89",
        background5: "#5d7850", // subtle accent background
        background6: "#040404", // subtle theme background.  complemented with fore*6
        foreground: "#5ca547",
        foreground1: "#f8f8f8", // main foreground color. in a dark theme, this should be light.
        foreground2: "#ffffff",
        foreground3: "#ffffff",
        foreground4: "#ffffff", // button complement and core accent color to background
        foreground5: "orange",
        foreground6: "#B3B3B3", // subtle.
        shadow: "teal",
        shadowHover: "green",
        foregroundHover: "#242425",
        foregroundHover1: "#373737",
        foregroundHover3: "#373737",
        foregroundHover2: "#373737",
        backgroundHover: "#6cc349", // hover background color for core accent.
        backgroundHover3: "#5a5856",
        backgroundHover2: "#5a5856",
        backgroundHover1: "#5a5856",
        backgroundDisabled: "pink",
        backgroundDisabled1: "pink",
        foregroundActive: "#cccccc",
        foregroundActive1: "#f8f8f8",
        foregroundActive2: "#f8f8f8",
        foregroundActive3: "#f8f8f8",
        borderActive3: "hotpink",
        borderActive2: "hotpink",
        borderActive1: "teal",
        borderActive: "purple",
        backgroundActive: "green",
        backgroundActive1: "#2a641c", // filled checkbox color
        borderFocusWithin: "red",
        backgroundFocus: "red",
        backgroundFocus1: "purple",
        backgroundFocus2: "#f8f8f8",
        backgroundFocus3: "#f8f8f8",
        foregroundFocus: "#cccccc",
        foregroundFocus1: "#f8f8f8",
        foregroundFocus2: "#f8f8f8",
        foregroundFocus3: "#f8f8f8",
        border: "hotpink",
        border1: "hotpink",
        border2: "hotpink",
        borderHover: "hotpink",
        borderFocus: "hotpink",
        borderFocus1: "#00A552",
        borderPressed: "hotpink",
        backgroundPressed: "#2a641c",
      },
    },
    colors: {
      brand: {
        "50": "#ffffff",
        "100": "#ffffff",
        "200": "#ffffff",
        "300": "pink",
        "400": "lightpink",
        "500": "hotpink",
        "600": "deeppink",
        "700": "palevioletred",
        "800": "black",
        "900": "black",
      },
      grey: {
        "50": "#F2F2F2",
        "100": "#E6E6E6",
        "200": "#CCCCCC",
        "300": "#B3B3B3",
        "400": "#999999",
        "500": "#737373",
        "600": "#666666",
        "700": "#4D4D4D",
        "800": "#333333",
        "900": "#1A1A1A",
      },
      white: "#FFF",
    },
  },
};

export const minecraftToolLightTheme = {
  siteVariables: {
    bodyFontFamily: '"Noto Sans", "Segoe UI", system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif',
    colorScheme: {
      brand: {
        mc0: "#131313",
        mc1: "#595759",
        mc2: "#656465",
        mc3: "#C0BFC0",
        mc4: "#C6C6C6",
        mc5: "#f7f7f7",
        mcc1: "#4C4C4C",
        background: "#6bc349", // accent background for things like buttons. complemented with fore*4
        background1: "#e6e4e2", // main outermost background. in a dark theme, this should be dark. complemented with fore*1
        background2: "#cccac7", // complement background. in a dark theme, this should be dark, but slightly different vs back*1. complemented with fore*2
        background3: "#bfbbb9",
        background4: "#7b7876",
        background5: "#52a435", // subtle accent background
        background6: "#b3b3b3", // subtle theme background.  complemented with fore*6
        foreground: "#3b9329",
        foreground1: "#1a1a1a", // main foreground color.  in a dark theme, this should be light.
        foreground2: "#1a1a1a", // complement foreground color
        foreground3: "#1a1a1a",
        foreground4: "#000000", // button complement and core accent color to background
        foreground5: "#1a1a1a",
        foreground6: "#4d4d4d", // subtle.
        shadow: "teal",
        shadowHover: "green",
        foregroundHover: "#242425",
        foregroundHover1: "pink",
        foregroundHover3: "pink",
        foregroundHover2: "pink",
        backgroundHover: "#6cc349", // hover background color for core accent.
        backgroundHover3: "pink",
        backgroundHover2: "pink",
        backgroundHover1: "pink",
        backgroundDisabled: "pink",
        backgroundDisabled1: "pink",
        foregroundActive: "#4d4d4d", // used for toolbar buttons
        foregroundActive1: "hotpink",
        foregroundActive2: "hotpink",
        foregroundActive3: "pink",
        foregroundActive4: "pink",
        foregroundActive5: "pink",
        foregroundActive6: "pink",
        borderActive3: "hotpink",
        borderActive2: "hotpink",
        borderActive1: "teal",
        borderActive: "purple",
        backgroundActive: "green",
        backgroundActive1: "#2a641c", // filled checkbox color
        borderFocusWithin: "red",
        backgroundFocus: "red",
        backgroundFocus1: "purple",
        backgroundFocus2: "hotpink",
        backgroundFocus3: "hotpink",
        foregroundFocus: "#CCCCCC",
        foregroundFocus1: "hotpink",
        foregroundFocus2: "hotpink",
        foregroundFocus3: "hotpink",
        border: "hotpink",
        border1: "hotpink",
        border2: "hotpink",
        borderHover: "hotpink",
        borderFocus: "hotpink",
        borderFocus1: "#00A552",
        borderPressed: "hotpink",
        backgroundPressed: "#2a641c",
      },
    },
    colors: {
      brand: {
        "50": "#ffffff",
        "100": "#ffffff",
        "200": "#ffffff",
        "300": "pink",
        "400": "lightpink",
        "500": "hotpink",
        "600": "deeppink",
        "700": "palevioletred",
        "800": "black",
        "900": "black",
      },
      grey: {
        "50": "#F2F2F2",
        "100": "#E6E6E6",
        "200": "#CCCCCC",
        "300": "#B3B3B3",
        "400": "#999999",
        "500": "#737373",
        "600": "#666666",
        "700": "#4D4D4D",
        "800": "#333333",
        "900": "#1A1A1A",
      },
      white: "#FFF",
    },
  },
};
